/**
 * One list of home answers for onboarding, the agent form, Property Details,
 * CSV import, and the API. Stored on the existing client columns.
 * CommonJS only. No object spread.
 */

const NOT_SURE = 'not_sure';
const HOUSE_PROPERTY_TYPES = ['single_family', 'townhome', 'mobile_home'];

const CONSTRUCTION_OPTIONS = [
  { id: 'concrete_block', label: 'Concrete block (CBS)', description: 'Walls made of concrete blocks. Common in Florida.' },
  { id: 'wood_frame', label: 'Wood Frame', description: 'Wood studs hold the walls up.' },
  { id: 'steel_frame', label: 'Steel frame', description: 'Metal studs hold the walls up.' },
  { id: 'icf', label: 'ICF', description: 'Foam blocks filled with concrete.' },
  { id: 'manufactured', label: 'Manufactured/mobile', description: 'Built in a factory, then set on site.' },
  { id: NOT_SURE, label: 'Not sure', description: 'That is okay. We will skip the special tasks.' },
];

const EXTERIOR_OPTIONS = [
  { id: 'stucco', label: 'Stucco', description: 'Smooth or textured painted wall.' },
  { id: 'vinyl_siding', label: 'Vinyl siding', description: 'Plastic panels that click together.' },
  { id: 'fiber_cement', label: 'Fiber cement (Hardie board)', description: 'Looks like wood, but it is cement.' },
  { id: 'wood_siding', label: 'Wood siding', description: 'Real wood boards on the outside.' },
  { id: 'brick', label: 'Brick', description: 'Brick you can see on the outside.' },
  { id: 'stone', label: 'Stone', description: 'Stone or stone-look pieces on the outside.' },
  { id: 'metal', label: 'Metal', description: 'Metal panels on the walls.' },
  { id: 'eifs', label: 'EIFS', description: 'A foam wall with a thin coat on top.' },
  { id: NOT_SURE, label: 'Not sure', description: 'That is okay. We will skip the special tasks.' },
];

const FOUNDATION_OPTIONS = [
  { id: 'slab', label: 'Slab', description: 'The house sits on a concrete pad.' },
  { id: 'crawl_space', label: 'Crawlspace', description: 'A short space under the house you can crawl in.' },
  { id: 'stem_wall', label: 'Stem wall', description: 'Short concrete walls with a floor on top.' },
  { id: 'pier_beam', label: 'Pier and beam / pilings', description: 'The house sits on posts or pilings.' },
  { id: NOT_SURE, label: 'Not sure', description: 'That is okay. We will skip the special tasks.' },
];

const ROOF_OPTIONS = [
  { id: 'asphalt_shingles', label: 'Asphalt shingle', description: 'The common layered shingles.' },
  { id: 'concrete_tile', label: 'Concrete tile', description: 'Heavy tiles, often S-shaped or flat.' },
  { id: 'clay_tile', label: 'Clay tile', description: 'Barrel tiles made of clay.' },
  { id: 'metal', label: 'Metal', description: 'Standing seam or 5V crimp panels.' },
  { id: 'flat_tpo', label: 'Flat roof', description: 'Modified bitumen or TPO. Low slope.' },
  { id: NOT_SURE, label: 'Not sure', description: 'That is okay. We will skip the special tasks.' },
];

const CONSTRUCTION_ALIASES = {
  concrete_block: 'concrete_block',
  concreteblock: 'concrete_block',
  cbs: 'concrete_block',
  concrete: 'concrete_block',
  block: 'concrete_block',
  masonry: 'concrete_block',
  wood_frame: 'wood_frame',
  woodframe: 'wood_frame',
  wood_framed: 'wood_frame',
  frame: 'wood_frame',
  steel_frame: 'steel_frame',
  steelframe: 'steel_frame',
  steel: 'steel_frame',
  metal_frame: 'steel_frame',
  icf: 'icf',
  insulated_concrete_forms: 'icf',
  insulated_concrete_form: 'icf',
  manufactured: 'manufactured',
  manufactured_home: 'manufactured',
  mobile: 'manufactured',
  not_sure: NOT_SURE,
  unsure: NOT_SURE,
  unknown: NOT_SURE,
  idk: NOT_SURE,
};

const EXTERIOR_ALIASES = {
  stucco: 'stucco',
  cement_stucco: 'stucco',
  vinyl_siding: 'vinyl_siding',
  vinyl: 'vinyl_siding',
  fiber_cement: 'fiber_cement',
  fibercement: 'fiber_cement',
  hardie: 'fiber_cement',
  hardie_board: 'fiber_cement',
  hardieboard: 'fiber_cement',
  hardie_plank: 'fiber_cement',
  wood_siding: 'wood_siding',
  wood: 'wood_siding',
  brick: 'brick',
  brick_veneer: 'brick',
  brickveneer: 'brick',
  stone: 'stone',
  stone_veneer: 'stone',
  stoneveneer: 'stone',
  metal: 'metal',
  metal_siding: 'metal',
  aluminum: 'metal',
  aluminum_siding: 'metal',
  eifs: 'eifs',
  synthetic_stucco: 'eifs',
  not_sure: NOT_SURE,
  unsure: NOT_SURE,
  unknown: NOT_SURE,
};

const FOUNDATION_ALIASES = {
  slab: 'slab',
  slab_on_grade: 'slab',
  slabongrade: 'slab',
  crawl_space: 'crawl_space',
  crawlspace: 'crawl_space',
  crawl: 'crawl_space',
  stem_wall: 'stem_wall',
  stemwall: 'stem_wall',
  stem: 'stem_wall',
  pier_beam: 'pier_beam',
  pier: 'pier_beam',
  piers: 'pier_beam',
  pilings: 'pier_beam',
  piling: 'pier_beam',
  pier_and_beam: 'pier_beam',
  stilts: 'pier_beam',
  not_sure: NOT_SURE,
  unsure: NOT_SURE,
  unknown: NOT_SURE,
};

const FOUNDATION_LEGACY = {
  full_basement: 'Full basement',
  partial_basement: 'Partial basement',
  basement: 'Full basement',
};

const CONSTRUCTION_LEGACY = {
  brick: 'Brick',
  log_timber: 'Log/timber frame',
  log: 'Log/timber frame',
  timber: 'Log/timber frame',
};

const ROOF_ALIASES = {
  asphalt_shingles: 'asphalt_shingles',
  asphalt_shingle: 'asphalt_shingles',
  asphalt: 'asphalt_shingles',
  shingle: 'asphalt_shingles',
  shingles: 'asphalt_shingles',
  concrete_tile: 'concrete_tile',
  concretetile: 'concrete_tile',
  clay_tile: 'clay_tile',
  claytile: 'clay_tile',
  clay: 'clay_tile',
  tile: 'tile',
  metal: 'metal',
  standing_seam: 'metal',
  '5v': 'metal',
  '5v_crimp': 'metal',
  metal_roof: 'metal',
  flat_tpo: 'flat_tpo',
  flat: 'flat_tpo',
  tpo: 'flat_tpo',
  modified_bitumen: 'flat_tpo',
  mod_bit: 'flat_tpo',
  not_sure: NOT_SURE,
  unsure: NOT_SURE,
  unknown: NOT_SURE,
};

const ROOF_LEGACY = {
  tile: 'Tile (clay or concrete)',
  slate: 'Slate',
  wood_shake: 'Wood shake',
  woodshake: 'Wood shake',
};

const TILE_ROOFS = { tile: true, concrete_tile: true, clay_tile: true };

function aliasKey(value) {
  return String(value == null ? '' : value)
    .trim()
    .toLowerCase()
    .replace(/['’]/g, '')
    .replace(/[\s-]+/g, '_')
    .replace(/_+/g, '_');
}

function optionById(options, id) {
  for (let i = 0; i < options.length; i += 1) {
    if (options[i].id === id) return options[i];
  }
  return null;
}

function labelFrom(options, id, legacyMap) {
  const option = optionById(options, id);
  if (option) return option.label;
  if (legacyMap && legacyMap[id]) return legacyMap[id];
  return '';
}

function mapAlias(map, value) {
  const key = aliasKey(value);
  if (!key) return '';
  return map[key] || '';
}

function canonicalConstruction(value) {
  return mapAlias(CONSTRUCTION_ALIASES, value);
}

function canonicalExteriorId(value) {
  return mapAlias(EXTERIOR_ALIASES, value);
}

function canonicalFoundation(value) {
  const mapped = mapAlias(FOUNDATION_ALIASES, value);
  if (mapped) return mapped;
  const key = aliasKey(value);
  if (FOUNDATION_LEGACY[key]) return key === 'basement' ? 'full_basement' : key;
  return '';
}

function canonicalRoof(value) {
  const mapped = mapAlias(ROOF_ALIASES, value);
  if (mapped) return mapped;
  const key = aliasKey(value);
  if (key === 'slate' || key === 'wood_shake' || key === 'woodshake') {
    return key === 'woodshake' ? 'wood_shake' : key;
  }
  return '';
}

function keptLegacyConstruction(value) {
  const key = aliasKey(value);
  if (!key || canonicalConstruction(value)) return '';
  return CONSTRUCTION_LEGACY[key] ? key : '';
}

function splitList(value) {
  if (Array.isArray(value)) return value;
  return String(value == null ? '' : value)
    .split(/\s*(?:\||,|;|\/|\band\b)\s*/i)
    .map((part) => part.trim())
    .filter(Boolean);
}

function parseExteriorList(value) {
  const ids = [];
  splitList(value).forEach((part) => {
    const id = canonicalExteriorId(part);
    if (id && ids.indexOf(id) === -1) ids.push(id);
  });
  if (ids.indexOf(NOT_SURE) !== -1) return [NOT_SURE];
  return ids;
}

function parseRoofList(value) {
  const ids = [];
  splitList(value).forEach((part) => {
    const id = canonicalRoof(part);
    if (id && ids.indexOf(id) === -1) ids.push(id);
  });
  if (ids.indexOf(NOT_SURE) !== -1) return [NOT_SURE];
  return ids;
}

function joinRoof(ids) {
  const clean = [];
  (ids || []).forEach((id) => {
    const canonical = canonicalRoof(id) || (optionById(ROOF_OPTIONS, id) ? id : '');
    if (canonical && clean.indexOf(canonical) === -1) clean.push(canonical);
  });
  if (clean.indexOf(NOT_SURE) !== -1) return NOT_SURE;
  return clean.join('|');
}

const HEATER_SYSTEMS = [
  { id: 'Electric Tank', label: 'Tank, electric', description: 'A tank that heats with electricity.', kind: 'tank' },
  { id: 'Gas Tank', label: 'Tank, gas', description: 'A tank that heats with gas.', kind: 'tank' },
  { id: 'Electric Tankless', label: 'Tankless, electric', description: 'Heats water as it is used, with electricity.', kind: 'tankless' },
  { id: 'Gas Tankless', label: 'Tankless, gas', description: 'Heats water as it is used, with gas.', kind: 'tankless' },
  { id: 'Heat Pump Water Heater', label: 'Heat pump', description: 'A tank that pulls heat from the air.', kind: 'tank' },
  { id: 'Solar Water Heater', label: 'Solar', description: 'The sun heats the water.', kind: 'solar' },
];
const HEATER_OPTIONS = HEATER_SYSTEMS;

const HVAC_SYSTEMS = [
  { id: 'central', label: 'Central', description: 'Ducted. This one has a filter.' },
  { id: 'heat_pump', label: 'Heat pump', description: 'Ducted heating and cooling.' },
  { id: 'mini_split', label: 'Mini split', description: 'A ductless head on the wall.' },
  { id: 'window', label: 'Window unit', description: 'Sits in a window.' },
];

function canonicalHeater(value) {
  const key = aliasKey(value).replace(/[()]/g, '');
  if (key === 'electric_tank' || key === 'tank_electric' || key === 'electric') return 'Electric Tank';
  if (key === 'gas_tank' || key === 'tank_gas' || key === 'gas') return 'Gas Tank';
  if (key === 'electric_tankless' || key === 'tankless_electric') return 'Electric Tankless';
  if (key === 'gas_tankless' || key === 'tankless_gas') return 'Gas Tankless';
  if (key === 'heat_pump' || key === 'heat_pump_water_heater') return 'Heat Pump Water Heater';
  if (key === 'solar' || key === 'solar_water_heater') return 'Solar Water Heater';
  if (key === 'tank') return 'Gas Tank';
  if (key === 'tankless') return 'Gas Tankless';
  const text = String(value || '').trim();
  if (!text) return '';
  return HEATER_SYSTEMS.some((row) => row.id === text) ? text : '';
}

function parseHeaterToken(part) {
  const text = String(part || '').trim();
  if (!text) return null;
  let name = text;
  let year = null;
  let count = 1;
  const at = name.lastIndexOf('@');
  if (at > 0 && /^\d{4}$/.test(name.slice(at + 1))) {
    year = Number(name.slice(at + 1));
    name = name.slice(0, at).trim();
  }
  const colon = name.lastIndexOf(':');
  if (colon > 0 && /^\d+$/.test(name.slice(colon + 1))) {
    count = Math.min(8, Math.floor(Number(name.slice(colon + 1))));
    name = name.slice(0, colon).trim();
  }
  const id = canonicalHeater(name);
  if (!id || count < 1) return null;
  if (!HEATER_SYSTEMS.some((row) => row.id === id)) return null;
  return { id, count, year };
}

function heaterUnits(value, legacyYear) {
  const slots = [];
  splitList(value).forEach((part) => {
    const token = parseHeaterToken(part);
    if (!token) return;
    for (let i = 0; i < token.count; i += 1) slots.push({ id: token.id, year: token.year });
  });
  const legacy = Math.floor(Number(legacyYear));
  if (legacy >= 1900 && slots.length && slots.every((slot) => !slot.year)) {
    slots.forEach((slot) => { slot.year = legacy; });
  }
  return slots;
}

function heaterUnitSlots(value, legacyYear) {
  const slots = heaterUnits(value, legacyYear);
  const totals = {};
  slots.forEach((slot) => { totals[slot.id] = (totals[slot.id] || 0) + 1; });
  const seen = {};
  return slots.map((slot) => {
    const row = HEATER_SYSTEMS.filter((item) => item.id === slot.id)[0];
    seen[slot.id] = (seen[slot.id] || 0) + 1;
    const label = totals[slot.id] > 1 ? row.label + ' ' + seen[slot.id] : row.label;
    return { id: slot.id, kind: row.kind, year: slot.year, label };
  });
}

function encodeHeaterType(value, legacyYear) {
  const grouped = {};
  HEATER_SYSTEMS.forEach((row) => { grouped[row.id] = []; });
  heaterUnits(value, legacyYear).forEach((slot) => {
    if (!grouped[slot.id]) grouped[slot.id] = [];
    grouped[slot.id].push(slot.year || null);
  });
  const parts = [];
  HEATER_SYSTEMS.forEach((row) => {
    grouped[row.id].forEach((year) => {
      parts.push(year ? row.id + '@' + year : row.id);
    });
  });
  return parts.join('|');
}

function oldestHeaterYear(value, legacyYear) {
  const years = heaterUnits(value, legacyYear).map((slot) => slot.year).filter((year) => year);
  if (!years.length) return null;
  return Math.min.apply(null, years);
}

function heaterSummary(value, legacyYear) {
  return heaterUnitSlots(value, legacyYear).map((slot) => (
    slot.year ? slot.label + ' (' + slot.year + ')' : slot.label
  )).join(', ');
}

function parseHeaterList(value) {
  const ids = [];
  heaterUnits(value, null).forEach((slot) => {
    if (ids.indexOf(slot.id) === -1) ids.push(slot.id);
  });
  return ids;
}

function joinHeater(ids) {
  const clean = [];
  (ids || []).forEach((id) => {
    const canonical = canonicalHeater(id);
    if (canonical && clean.indexOf(canonical) === -1) clean.push(canonical);
  });
  return clean.join('|');
}

function canonicalHvacId(value) {
  const key = aliasKey(value).replace(/\//g, '_');
  if (key === 'central' || key === 'central_ac' || key === 'package' || key === 'package_unit') return 'central';
  if (key === 'heat_pump' || key === 'heatpump') return 'heat_pump';
  if (key === 'mini_split' || key === 'mini_split_ductless' || key === 'minisplit' || key === 'ductless') return 'mini_split';
  if (key === 'window' || key === 'window_unit' || key === 'window_units') return 'window';
  return HVAC_SYSTEMS.some((row) => row.id === key) ? key : '';
}

function parseHvacSystems(value, fallbackCount) {
  const systems = {};
  const raw = Array.isArray(value) ? value : splitList(value);
  raw.forEach((item) => {
    const text = String(item || '').trim();
    if (!text) return;
    const bits = text.split(':');
    const id = canonicalHvacId(bits[0]);
    if (!id) return;
    const count = bits.length > 1 ? Math.floor(Number(bits[1])) : 1;
    if (!count || count < 1) return;
    systems[id] = Math.min(8, (systems[id] || 0) + count);
  });
  if (!Object.keys(systems).length) {
    const count = Math.floor(Number(fallbackCount));
    if (count > 0) systems.central = Math.min(8, count);
  }
  return systems;
}

function hvacUnitSlots(home) {
  const source = home || {};
  const hasTypes = Array.isArray(source.hvac_types)
    ? source.hvac_types.length > 0
    : String(source.hvac_types || '').trim() !== '';
  const systems = parseHvacSystems(source.hvac_types, hasTypes ? null : source.hvac_count);
  const slots = [];
  HVAC_SYSTEMS.forEach((row) => {
    const count = systems[row.id] || 0;
    const ducted = row.id === 'central' || row.id === 'heat_pump';
    for (let i = 0; i < count; i += 1) {
      slots.push({
        id: row.id,
        ducted,
        label: count > 1 ? row.label + ' ' + (i + 1) : row.label,
      });
    }
  });
  return slots;
}

function readHvacUnitYears(home) {
  const source = home || {};
  const slots = hvacUnitSlots(source);
  const stored = Array.isArray(source.hvac_install_years) ? source.hvac_install_years : [];
  const numbers = stored.map((item) => {
    if (item == null || item === '') return null;
    const year = Math.floor(Number(item));
    return year >= 1900 ? year : null;
  });
  const ducted = slots.filter((slot) => slot.ducted).length;
  if (numbers.length === slots.length) return numbers;
  if (numbers.length && numbers.length === ducted) {
    let cursor = 0;
    return slots.map((slot) => (slot.ducted ? numbers[cursor++] : null));
  }
  const next = [];
  for (let i = 0; i < slots.length; i += 1) next.push(i < numbers.length ? numbers[i] : null);
  return next;
}

function hvacTypesField(systems) {
  return Object.keys(systems).filter((id) => systems[id] > 0).map((id) => id + ':' + systems[id]);
}

function ductedHvacCount(home) {
  const source = home || {};
  const hasTypes = Array.isArray(source.hvac_types)
    ? source.hvac_types.length > 0
    : String(source.hvac_types || '').trim() !== '';
  const systems = parseHvacSystems(source.hvac_types, hasTypes ? null : source.hvac_count);
  return (systems.central || 0) + (systems.heat_pump || 0);
}

function hvacUnitTotal(home) {
  const source = home || {};
  const hasTypes = Array.isArray(source.hvac_types)
    ? source.hvac_types.length > 0
    : String(source.hvac_types || '').trim() !== '';
  const systems = parseHvacSystems(source.hvac_types, hasTypes ? null : source.hvac_count);
  return Object.keys(systems).reduce((sum, id) => sum + (systems[id] || 0), 0);
}

function hasHvacType(home, id) {
  const systems = parseHvacSystems(
    home && home.hvac_types,
    home && home.hvac_types ? null : (home && home.hvac_count)
  );
  return (systems[id] || 0) > 0;
}

function joinExterior(ids) {
  const clean = [];
  (ids || []).forEach((id) => {
    const canonical = canonicalExteriorId(id) || (optionById(EXTERIOR_OPTIONS, id) ? id : '');
    if (canonical && clean.indexOf(canonical) === -1) clean.push(canonical);
  });
  if (clean.indexOf(NOT_SURE) !== -1) return NOT_SURE;
  return clean.join('|');
}

function listForKind(kind, current) {
  if (kind === 'roof') return parseRoofList(current);
  if (kind === 'heater') return parseHeaterList(current);
  return parseExteriorList(current);
}

function canonicalForKind(kind, id) {
  if (kind === 'roof') return canonicalRoof(id) || id;
  if (kind === 'heater') return canonicalHeater(id) || id;
  return canonicalExteriorId(id) || id;
}

function joinForKind(kind, ids) {
  if (kind === 'roof') return joinRoof(ids);
  if (kind === 'heater') return joinHeater(ids);
  return joinExterior(ids);
}

function toggleHomeChoice(current, id, multi, kind) {
  const wanted = multi ? canonicalForKind(kind, id) : id;
  if (!multi) {
    return current === wanted ? '' : wanted;
  }
  let ids = listForKind(kind, current);
  if (wanted === NOT_SURE) {
    return ids.length === 1 && ids[0] === NOT_SURE ? '' : NOT_SURE;
  }
  ids = ids.filter((item) => item !== NOT_SURE);
  if (ids.indexOf(wanted) === -1) ids.push(wanted);
  else ids = ids.filter((item) => item !== wanted);
  return joinForKind(kind, ids);
}

function choiceSelected(current, id, multi, kind) {
  if (multi) return listForKind(kind, current).indexOf(canonicalForKind(kind, id)) !== -1;
  return current === id;
}

function constructionLabel(value) {
  const id = canonicalConstruction(value) || keptLegacyConstruction(value);
  if (!id) return '';
  return labelFrom(CONSTRUCTION_OPTIONS, id, CONSTRUCTION_LEGACY);
}

function exteriorLabel(value) {
  const ids = parseExteriorList(value);
  if (!ids.length) return '';
  return ids.map((id) => labelFrom(EXTERIOR_OPTIONS, id, null)).filter(Boolean).join(', ');
}

function foundationLabel(value) {
  const id = canonicalFoundation(value);
  if (!id) return '';
  return labelFrom(FOUNDATION_OPTIONS, id, FOUNDATION_LEGACY);
}

function roofMaterialLabel(value) {
  const ids = parseRoofList(value);
  if (ids.length) {
    return ids.map((id) => labelFrom(ROOF_OPTIONS, id, ROOF_LEGACY) || id).filter(Boolean).join(', ');
  }
  const id = canonicalRoof(value);
  if (!id) return '';
  return labelFrom(ROOF_OPTIONS, id, ROOF_LEGACY);
}

function hasOwn(source, key) {
  return !!source && Object.prototype.hasOwnProperty.call(source, key);
}

function firstPresent(source, keys) {
  for (let i = 0; i < keys.length; i += 1) {
    if (hasOwn(source, keys[i])) return { present: true, value: source[keys[i]] };
  }
  return { present: false, value: undefined };
}

function blank(value) {
  return value == null || String(value).trim() === '';
}

function yearNumber(value, nowYear) {
  if (blank(value)) return { empty: true };
  const key = aliasKey(value);
  if (key === 'not_sure' || key === 'unsure' || key === 'unknown') return { empty: true };
  const buckets = { '0_5': 2, '6_10': 8, '11_15': 13, '16': 20, '16_plus': 20 };
  if (Object.prototype.hasOwnProperty.call(buckets, key)) {
    return { year: nowYear - buckets[key] };
  }
  if (!/^\d{4}$/.test(String(value).trim())) return { error: 'Use a 4-digit year' };
  const year = Number(value);
  if (year < 1900 || year > nowYear + 1) return { error: 'Use a year from 1900 to ' + (nowYear + 1) };
  return { year };
}

function homeProfileColumns(source, nowYear) {
  const input = source && typeof source === 'object' ? source : {};
  const currentYear = nowYear || new Date().getFullYear();
  const patch = {};
  const errors = {};
  const warnings = [];

  const construction = firstPresent(input, ['construction_type', 'construction']);
  if (construction.present) {
    if (blank(construction.value)) patch.construction_type = null;
    else {
      const id = canonicalConstruction(construction.value) || keptLegacyConstruction(construction.value);
      if (!id) errors.construction_type = 'Unknown construction';
      else {
        patch.construction_type = id;
        if (keptLegacyConstruction(construction.value)) {
          warnings.push('Saved construction "' + constructionLabel(id) + '" is an older answer. Pick the closest match when you can.');
        }
      }
    }
  }

  const exterior = firstPresent(input, ['exterior_finish', 'exterior', 'exteriors']);
  if (exterior.present) {
    if (blank(exterior.value) || (Array.isArray(exterior.value) && exterior.value.length === 0)) {
      patch.exterior_finish = null;
    } else {
      const parts = splitList(exterior.value);
      const ids = [];
      parts.forEach((part) => {
        const id = canonicalExteriorId(part);
        if (!id) warnings.push('Unknown exterior "' + String(part).trim() + '" was dropped');
        else if (ids.indexOf(id) === -1) ids.push(id);
      });
      if (!ids.length) errors.exterior_finish = 'Unknown exterior';
      else patch.exterior_finish = joinExterior(ids);
    }
  }

  const foundation = firstPresent(input, ['foundation_type', 'foundation']);
  if (foundation.present) {
    if (blank(foundation.value)) patch.foundation_type = null;
    else {
      const id = canonicalFoundation(foundation.value);
      if (!id) errors.foundation_type = 'Unknown foundation';
      else {
        patch.foundation_type = id;
        if (FOUNDATION_LEGACY[id]) {
          warnings.push('Saved foundation "' + foundationLabel(id) + '" is an older answer. Pick the closest match when you can.');
        }
      }
    }
  }

  const roof = firstPresent(input, ['roof_material', 'roof', 'roofs']);
  if (roof.present) {
    if (blank(roof.value) || (Array.isArray(roof.value) && roof.value.length === 0)) {
      patch.roof_material = null;
    } else {
      const ids = parseRoofList(roof.value);
      if (!ids.length) errors.roof_material = 'Unknown roof';
      else patch.roof_material = joinRoof(ids);
    }
  }

  const year = firstPresent(input, ['roof_year', 'roof_install_year', 'roof_age']);
  if (year.present) {
    const parsed = yearNumber(year.value, currentYear);
    if (parsed.error) errors.roof_year = parsed.error;
    else {
      const stored = parsed.empty ? null : parsed.year;
      patch.roof_year = stored;
      if (!hasOwn(input, 'roof_install_year') || hasOwn(input, 'roof_year') || hasOwn(input, 'roof_age')) {
        patch.roof_install_year = stored;
      }
    }
  }
  if (hasOwn(input, 'roof_install_year') && !hasOwn(input, 'roof_year') && !hasOwn(input, 'roof_age')) {
    const parsed = yearNumber(input.roof_install_year, currentYear);
    if (!parsed.error) {
      patch.roof_install_year = parsed.empty ? null : parsed.year;
      if (!hasOwn(patch, 'roof_year')) patch.roof_year = patch.roof_install_year;
    }
  }

  if (hasOwn(input, 'hvac_types') || hasOwn(input, 'hvac_systems')) {
    const raw = input.hvac_types || input.hvac_systems;
    const empty = raw == null || (Array.isArray(raw) && raw.length === 0) || raw === '';
    if (!empty) {
      const systems = parseHvacSystems(raw, null);
      const field = hvacTypesField(systems);
      patch.hvac_types = field;
      patch.hvac_count = hvacUnitTotal({ hvac_types: field }) || null;
    }
  }

  if (hasOwn(input, 'water_heater_type') || hasOwn(input, 'water_heaters')) {
    const encoded = encodeHeaterType(input.water_heater_type || input.water_heaters, input.water_heater_install_year);
    patch.water_heater_type = encoded || null;
    if (encoded) patch.water_heater_install_year = oldestHeaterYear(encoded, null);
  }

  if (hasOwn(input, 'water_heater_install_year') && !hasOwn(patch, 'water_heater_install_year')) {
    const year = Math.floor(Number(input.water_heater_install_year));
    patch.water_heater_install_year = year >= 1900 && year <= currentYear ? year : null;
  }

  const hvac = firstPresent(input, ['hvac_count', 'ac_count']);
  if (hvac.present && !hasOwn(patch, 'hvac_types')) {
    if (blank(hvac.value)) patch.hvac_count = null;
    else if (!/^\d+$/.test(String(hvac.value).trim())) errors.hvac_count = 'Use a whole number';
    else {
      const count = Number(hvac.value);
      if (count < 1 || count > 8) errors.hvac_count = 'Use 1 to 8 air conditioners';
      else patch.hvac_count = count;
    }
  }

  return { patch, errors, warnings };
}

function readHomeProfile(source, nowYear) {
  const row = source || {};
  const parsed = homeProfileColumns({
    construction_type: row.construction_type == null ? '' : row.construction_type,
    exterior_finish: row.exterior_finish == null ? '' : row.exterior_finish,
    foundation_type: row.foundation_type == null ? '' : row.foundation_type,
    roof_material: row.roof_material == null ? '' : row.roof_material,
    roof_year: row.roof_year == null || row.roof_year === '' ? row.roof_install_year : row.roof_year,
    hvac_count: row.hvac_count == null || row.hvac_count === '' ? '' : row.hvac_count,
    hvac_types: row.hvac_types || [],
    water_heater_type: row.water_heater_type == null ? '' : row.water_heater_type,
    water_heater_install_year: row.water_heater_install_year || '',
  }, nowYear);
  const patch = parsed.patch;
  return {
    construction_type: patch.construction_type || '',
    exterior_finish: patch.exterior_finish || '',
    foundation_type: patch.foundation_type || '',
    roof_material: patch.roof_material || '',
    roof_year: patch.roof_year || null,
    hvac_count: patch.hvac_count != null ? patch.hvac_count : (row.hvac_count || null),
    hvac_types: patch.hvac_types || (Array.isArray(row.hvac_types) ? row.hvac_types : []),
    water_heater_type: patch.water_heater_type || '',
    water_heater_install_year: patch.water_heater_install_year || row.water_heater_install_year || '',
    warnings: parsed.warnings,
  };
}

function homeSetupFinished(client) {
  return Boolean(client && (client.onboarding_completed || client.onboarding_completed_at));
}

function homeProfileIsBlank(source) {
  const profile = readHomeProfile(source);
  return !profile.construction_type
    && !profile.exterior_finish
    && !profile.foundation_type
    && !profile.roof_material
    && !profile.roof_year;
}

function homeProfileSignature(source) {
  const profile = readHomeProfile(source);
  return [
    profile.construction_type,
    profile.exterior_finish,
    profile.foundation_type,
    profile.roof_material,
    profile.roof_year || '',
    profile.hvac_count || '',
    Array.isArray(profile.hvac_types) ? profile.hvac_types.join(',') : (profile.hvac_types || ''),
    profile.water_heater_type || '',
  ].join('~');
}

function homeProfileSavePatch(value, nowYear) {
  const parsed = homeProfileColumns(value || {}, nowYear);
  const patch = parsed.patch;
  if (!hasOwn(patch, 'roof_install_year') && hasOwn(patch, 'roof_year')) {
    patch.roof_install_year = patch.roof_year;
  }
  return patch;
}

function waterHeaterKind(type) {
  const text = String(type || '').trim().toLowerCase();
  if (!text) return null;
  if (text.indexOf('tankless') !== -1) return 'tankless';
  if (text.indexOf('solar') !== -1) return 'solar';
  if (text.indexOf('tank') !== -1 || text.indexOf('heat pump') !== -1) return 'tank';
  return null;
}

function waterHeaterKinds(type) {
  const kinds = [];
  parseHeaterList(type).forEach((id) => {
    const kind = waterHeaterKind(id);
    if (kind && kinds.indexOf(kind) === -1) kinds.push(kind);
  });
  return kinds;
}

function installAgeYears(year, now) {
  const installed = Number(year);
  const current = now ? new Date(now).getFullYear() : new Date().getFullYear();
  if (!installed || installed < 1900 || installed > current + 1) return null;
  return current - installed;
}

function roofAgeYears(home, now) {
  const year = Number(home && (home.roof_year || home.roof_install_year));
  const current = now ? new Date(now).getFullYear() : new Date().getFullYear();
  if (!year || year < 1800 || year > current + 1) return null;
  return current - year;
}

function valuesMatch(dimension, have, need) {
  return need.some((wanted) => have.some((got) => {
    if (got === wanted) return true;
    if (dimension === 'roof' && TILE_ROOFS[got] && TILE_ROOFS[wanted]) return true;
    return false;
  }));
}

function homeMatchesConditions(conditions, home, now) {
  if (!conditions || typeof conditions !== 'object') return true;
  const profile = readHomeProfile(home || {}, now ? new Date(now).getFullYear() : undefined);
  const checks = [];

  if (conditions.hvac_index != null || conditions.hvac_min != null || conditions.hvac_max != null) {
    const source = home || {};
    const listed = Array.isArray(source.hvac_types) && source.hvac_types.length > 0;
    const count = listed ? ductedHvacCount(source) : (Number(source.hvac_count) || profile.hvac_count || 1);
    const index = conditions.hvac_index == null ? null : Number(conditions.hvac_index);
    const min = conditions.hvac_min != null
      ? Number(conditions.hvac_min)
      : (index == null ? 1 : index);
    let ok = count >= min;
    if (conditions.hvac_max != null && count > Number(conditions.hvac_max)) ok = false;
    if (index != null && index > count) ok = false;
    checks.push(ok);
  }

  const groups = [
    ['construction', profile.construction_type ? [profile.construction_type] : []],
    ['exterior', parseExteriorList(profile.exterior_finish)],
    ['foundation', profile.foundation_type ? [profile.foundation_type] : []],
    ['roof', parseRoofList(profile.roof_material)],
  ];
  groups.forEach((group) => {
    const need = conditions[group[0]];
    if (!Array.isArray(need) || !need.length) return;
    checks.push(valuesMatch(group[0], group[1], need));
  });

  const sourceHome = home || {};
  if (Array.isArray(conditions.water_heater) && conditions.water_heater.length) {
    const kinds = waterHeaterKinds(sourceHome.water_heater_type);
    checks.push(kinds.some((kind) => conditions.water_heater.indexOf(kind) !== -1));
  }
  if (Array.isArray(conditions.hvac_type) && conditions.hvac_type.length) {
    checks.push(conditions.hvac_type.some((id) => hasHvacType(sourceHome, id)));
  }
  if (conditions.hvac_ducted === true) {
    checks.push(ductedHvacCount(sourceHome) > 0);
  }
  if (conditions.water_heater_age_min != null) {
    const slots = heaterUnitSlots(sourceHome.water_heater_type, sourceHome.water_heater_install_year);
    const needed = Array.isArray(conditions.water_heater) ? conditions.water_heater : [];
    let oldEnough = false;
    slots.forEach((slot) => {
      if (needed.length && needed.indexOf(slot.kind) === -1) return;
      const age = installAgeYears(slot.year, now);
      if (age != null && age >= Number(conditions.water_heater_age_min)) oldEnough = true;
    });
    if (!slots.length) {
      const age = installAgeYears(sourceHome.water_heater_install_year, now);
      oldEnough = age != null && age >= Number(conditions.water_heater_age_min);
    }
    checks.push(oldEnough);
  }

  const hasAge = conditions.roof_age_min != null || conditions.roof_age_max != null;
  if (hasAge || conditions.when_roof_age_unknown === true) {
    const age = roofAgeYears(profile, now);
    if (age == null) checks.push(conditions.when_roof_age_unknown === true && !hasAge ? true : conditions.when_roof_age_unknown === true);
    else if (hasAge) {
      let ok = true;
      if (conditions.roof_age_min != null && age < Number(conditions.roof_age_min)) ok = false;
      if (conditions.roof_age_max != null && age > Number(conditions.roof_age_max)) ok = false;
      checks.push(ok);
    }
  }

  if (!checks.length) return true;
  if (conditions.match === 'any') return checks.some(Boolean);
  return checks.every(Boolean);
}

const HOME_PROFILE_QUESTIONS = [
  { id: 'construction_type', title: 'What are the walls built from?', hint: 'Pick one.', multi: false, options: CONSTRUCTION_OPTIONS },
  { id: 'exterior_finish', title: 'What is on the outside of the walls?', hint: 'Pick every one you have.', multi: true, options: EXTERIOR_OPTIONS },
  { id: 'foundation_type', title: 'What is under the house?', hint: 'Pick one.', multi: false, options: FOUNDATION_OPTIONS },
  { id: 'roof_material', title: 'What is the roof made of?', hint: 'Pick every one you have.', multi: true, listKind: 'roof', options: ROOF_OPTIONS },
  { id: 'roof_year', title: 'What year did the roof go on?', hint: 'A guess is okay.', year: true },
];

function homeFactsInviteDone(source) {
  const profile = readHomeProfile(source);
  const types = Array.isArray(profile.hvac_types) ? profile.hvac_types.filter(Boolean) : [];
  return Boolean(profile.construction_type && profile.roof_material && profile.water_heater_type && types.length);
}

function homeFactsCardKey(clientId) {
  return 'pacarion.homeFactsCard.' + String(clientId || '');
}

function homeFactsCardDismissed(storage, clientId) {
  if (!storage || !clientId) return false;
  try {
    return storage.getItem(homeFactsCardKey(clientId)) === '1';
  } catch (error) {
    return false;
  }
}

function dismissHomeFactsCard(storage, clientId) {
  if (!storage || !clientId) return;
  try {
    storage.setItem(homeFactsCardKey(clientId), '1');
  } catch (error) {
    // A blocked write still lets this session close the card.
  }
}

function promptStorageKey(clientId) {
  return 'pacarion.homeProfilePrompt.' + String(clientId || '');
}

function homeProfilePromptDismissed(storage, clientId) {
  if (!storage || !clientId) return false;
  try {
    return storage.getItem(promptStorageKey(clientId)) === '1';
  } catch (error) {
    return false;
  }
}

function dismissHomeProfilePrompt(storage, clientId) {
  if (!storage || !clientId) return;
  try {
    storage.setItem(promptStorageKey(clientId), '1');
  } catch (error) {
    // A blocked write still lets this session close the card.
  }
}

module.exports = {
  NOT_SURE,
  HOUSE_PROPERTY_TYPES,
  CONSTRUCTION_OPTIONS,
  EXTERIOR_OPTIONS,
  FOUNDATION_OPTIONS,
  ROOF_OPTIONS,
  CONSTRUCTION_ALIASES,
  EXTERIOR_ALIASES,
  FOUNDATION_ALIASES,
  ROOF_ALIASES,
  CONSTRUCTION_LEGACY,
  FOUNDATION_LEGACY,
  ROOF_LEGACY,
  HOME_PROFILE_QUESTIONS,
  canonicalConstruction,
  canonicalExteriorId,
  canonicalFoundation,
  canonicalRoof,
  parseExteriorList,
  parseRoofList,
  joinExterior,
  joinRoof,
  parseHvacSystems,
  hvacTypesField,
  hvacUnitSlots,
  readHvacUnitYears,
  ductedHvacCount,
  hvacUnitTotal,
  hasHvacType,
  waterHeaterKinds,
  heaterUnitSlots,
  encodeHeaterType,
  oldestHeaterYear,
  heaterSummary,
  HVAC_SYSTEMS,
  HEATER_SYSTEMS,
  HEATER_OPTIONS,
  toggleHomeChoice,
  choiceSelected,
  constructionLabel,
  exteriorLabel,
  foundationLabel,
  roofMaterialLabel,
  homeProfileColumns,
  readHomeProfile,
  homeSetupFinished,
  homeProfileIsBlank,
  homeProfileSignature,
  homeProfileSavePatch,
  roofAgeYears,
  homeMatchesConditions,
  promptStorageKey,
  homeProfilePromptDismissed,
  dismissHomeProfilePrompt,
  homeFactsInviteDone,
  homeFactsCardDismissed,
  dismissHomeFactsCard,
};
